import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';

import api from '../../../utils/functions/api';
import { showAlert, confirmAsync } from '../../../utils/functions/globalDialog';
import EmployeeAutocomplete from './EmployeeAutocomplete';
import { formatEmployeeThaiName } from '../../../utils/functions/employeeName';

function displayName(emp) {
  if (!emp) {
    return '(ไม่พบข้อมูลพนักงาน)';
  }
  return formatEmployeeThaiName(emp);
}

export default function ExcludedEmployeeModal({ open, onClose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchRows = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`${process.env.REACT_APP_API_URL}/sso-excluded-employee`);
      setRows(data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchRows();
    }
  }, [open]);

  const handleCreate = async () => {
    if (!selectedEmployee) {
      showAlert('กรุณาเลือกพนักงาน');
      return;
    }
    setSaving(true);
    try {
      await api.post(`${process.env.REACT_APP_API_URL}/sso-excluded-employee`, {
        employee: selectedEmployee._id,
      });
      showAlert('เพิ่มรายชื่อยกเว้นสำเร็จ');
      setSelectedEmployee(null);
      setAddOpen(false);
      fetchRows();
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    if (!(await confirmAsync(`นำ "${displayName(row.employee)}" ออกจากรายการยกเว้นหรือไม่? (จะกลับมาส่ง ปกส. ตามปกติ)`))) {
      return;
    }
    await api.delete(`${process.env.REACT_APP_API_URL}/sso-excluded-employee/${row._id}`);
    fetchRows();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { maxHeight: '90vh' } }}>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" fontWeight={700}>พนักงานที่ไม่ต้องส่งประกันสังคม</Typography>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          พนักงานในรายการนี้จะไม่ถูกส่งเข้าไฟล์ ปกส. เลย และไม่ขึ้นในตารางสรุปเงินเดือนรวม (รายวัน/รายเดือน/ไม่ได้ลงเวลา) ทุกเดือน แม้จะมีข้อมูลเงินเดือน/ลงเวลาปกติก็ตาม
        </Typography>

        {!addOpen ? (
          <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setAddOpen(true)} sx={{ mb: 2 }}>
            เพิ่มพนักงานที่ยกเว้น
          </Button>
        ) : (
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Stack spacing={2}>
              <EmployeeAutocomplete
                value={selectedEmployee}
                onChange={setSelectedEmployee}
                placeholder="ค้นหาชื่อพนักงาน..."
              />
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button onClick={() => { setSelectedEmployee(null); setAddOpen(false); }}>ยกเลิก</Button>
                <Button variant="contained" onClick={handleCreate} disabled={saving}>
                  {saving ? 'กำลังบันทึก...' : 'เพิ่ม'}
                </Button>
              </Stack>
            </Stack>
          </Paper>
        )}

        {loading ? (
          <Skeleton variant="rounded" width="100%" height={160} animation="wave" />
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ชื่อ</TableCell>
                  <TableCell align="center">ลบ</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={2} align="center">
                      <Box sx={{ py: 3, color: 'text.secondary' }}>ยังไม่มีรายชื่อยกเว้น</Box>
                    </TableCell>
                  </TableRow>
                )}
                {rows.map((row) => (
                  <TableRow key={row._id} hover>
                    <TableCell>{displayName(row.employee)}</TableCell>
                    <TableCell align="center">
                      <IconButton size="small" onClick={() => handleDelete(row)}>
                        <DeleteIcon fontSize="small" color="error" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose}>ปิด</Button>
      </DialogActions>
    </Dialog>
  );
}
